/**
 * Foreground color for content that sits ON the publisher's primary
 * (accent) color — the shield icons inside the accent discs on the
 * initial loading screen, the consent screen and the verification
 * processing screen.
 *
 * These icons live in the same visual role as a filled Button's label:
 * foreground on the primary surface. App.js drives that label from
 * `styles.components.button.color`, so the icons follow the same value
 * and the branding reads end-to-end.
 *
 * When the publisher has NOT set a button text color we can't just
 * default to white — a light primary (e.g. a bright mint/cyan) leaves
 * a white icon invisible. In that case we derive the more readable of
 * white / near-black from the accent itself.
 */

// Mantine dark[7] — the same near-black the rest of the app uses for
// primary text in light mode.
const DARK_FOREGROUND = "#1A1B1E";
const LIGHT_FOREGROUND = "#fff";

// Button-text color the publisher uses elsewhere in the journey, as a
// URL param. Supports first paint, before the session has been fetched
// and DataContext exists.
export const readButtonColorFromUrl = () => {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("buttonColor");
  if (!raw) return null;
  return raw.startsWith("#") ? raw : `#${raw}`;
};

// Parse "#abc", "#aabbcc", "#aabbccdd" or "rgb()/rgba()" into [r,g,b].
// Anything else (named colors, gradients, CSS vars) returns null and the
// caller falls back to white, preserving today's behavior.
const toRgb = (color) => {
  if (typeof color !== "string") return null;
  const value = color.trim();

  if (value.startsWith("#")) {
    let hex = value.slice(1);
    if (hex.length === 3 || hex.length === 4) {
      hex = hex
        .slice(0, 3)
        .split("")
        .map((c) => c + c)
        .join("");
    }
    if (hex.length === 8) hex = hex.slice(0, 6);
    if (hex.length !== 6 || /[^0-9a-f]/i.test(hex)) return null;
    return [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16),
    ];
  }

  const match = value.match(/^rgba?\(([^)]+)\)$/i);
  if (!match) return null;
  const parts = match[1].split(",").map((p) => parseFloat(p));
  if (parts.length < 3 || parts.slice(0, 3).some((n) => Number.isNaN(n))) {
    return null;
  }
  return parts.slice(0, 3);
};

// WCAG relative luminance.
const relativeLuminance = ([r, g, b]) => {
  const channel = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

// Flip to the dark foreground only once the background is genuinely
// light. Strict "best contrast ratio" would tip at ~0.18 luminance and
// turn the shield dark on mid-tone brand colors that have always been
// paired with white (Mantine teal[6] #12b886 sits at 0.36, and Mantine
// itself renders white labels on it). 0.5 keeps those untouched while
// catching the bright mints, ambers and near-whites that made the icon
// invisible.
const LIGHT_BACKGROUND_LUMINANCE = 0.5;

/**
 * White, or near-black when `background` is light enough that white
 * would wash out. Unparseable colors fall back to white.
 */
export const contrastingForeground = (background) => {
  const rgb = toRgb(background);
  if (!rgb) return LIGHT_FOREGROUND;
  return relativeLuminance(rgb) > LIGHT_BACKGROUND_LUMINANCE
    ? DARK_FOREGROUND
    : LIGHT_FOREGROUND;
};

/**
 * Resolve the icon color for content drawn on the accent surface.
 *
 * @param {object} args
 * @param {string|null} args.buttonTextColor publisher's `styles.components.button.color`
 * @param {string|null} args.accent          the primary color behind the icon
 */
export const resolveOnAccentColor = ({ buttonTextColor, accent }) =>
  buttonTextColor || readButtonColorFromUrl() || contrastingForeground(accent);

export default resolveOnAccentColor;
